'use client';
import { debounce } from '@smartproxy-web/shared/utils';
import { useEffect, useState } from 'react';

import { cn } from '@/lib/utils';
import useHash from '@/hooks/useHash';

import Button from '@/components/atoms/button';
import { blogContentId } from '@/components/atoms/layout/CMSBlogLayout';
import { Text } from '@/components/atoms/text';

export type SideNavigationProps = {
  title: string;
  formattedData?: string[];
};

// TODO extract logic and make it reusable with various props for enabling/disabling features
export function SideNavigationStrapi({
  title,
  formattedData = [],
}: SideNavigationProps) {
  const { hash } = useHash();
  const hashPrefix = 'h2';

  const [stateHash, setHash] = useState(hash);
  const [isScrolling, setIsScrolling] = useState(false);
  const [h2Elements, setH2Elements] = useState<HTMLHeadingElement[]>([]);

  const headings = formattedData;

  function scrollToPosition(x: number, y: number) {
    window.scrollTo(x, y);
    setIsScrolling(true);

    setTimeout(() => {
      setIsScrolling(false);
    }, 1000);
  }

  function getAndSetH2Elements() {
    const mainContentElement = document.getElementById(blogContentId);
    if (!mainContentElement) return;

    const h2Elements = mainContentElement.querySelectorAll('h2');

    setH2Elements(Array.from(h2Elements));
    return Array.from(h2Elements);
  }

  function handleScroll(h2Elements: HTMLHeadingElement[]) {
    if (!h2Elements.length || isScrolling) return;

    let targetElement: HTMLHeadingElement | null = null;

    // we want to find the nearest h2 element to the top of the screen
    for (let i = 0; i < h2Elements.length; i++) {
      const { top } = h2Elements[i].getBoundingClientRect();
      const { innerHeight } = window;

      const percentageFromTop = 0.12;
      if (top > innerHeight * percentageFromTop) continue;

      targetElement = h2Elements[i];
    }

    // when nearest is found we always put the hash of it in the url
    if (targetElement) {
      const text = trasnformTextToUrlHash(
        (targetElement as HTMLElement).innerText
      );
      if (text !== stateHash && hash !== '#contact-sales') {
        window.history.replaceState(null, '', text);
        setHash(text);
      }
    } else {
      window.history.replaceState(null, '', '');
      setHash('');
    }
  }

  useEffect(() => {
    const debouncedScroll = debounce(() => handleScroll(h2Elements), 50);

    window.addEventListener('scroll', debouncedScroll);
    return () => {
      window.removeEventListener('scroll', debouncedScroll);
    };
  }, [stateHash, h2Elements.length, isScrolling, hash]);

  useEffect(() => {
    const tempH2Elements = getAndSetH2Elements();
    if (tempH2Elements) {
      const tempHash = window.location.hash;
      if (tempHash) {
        handleScroll(tempH2Elements);
        handleHashChange(tempH2Elements);
      } else {
        scrollToPosition(0, 0);
      }
    }
  }, []);

  function handleHashChange(h2ElementsInner?: HTMLHeadingElement[]) {
    const newHash = window.location.hash;

    if (newHash.includes(`${hashPrefix}-`)) {
      const text = transformUrlHashToText(newHash);

      scrollToH2Element(text, h2ElementsInner || h2Elements);
    }

    setHash(newHash);
  }

  function cleanupLanguageText(text: string) {
    // we want cleanup for all languages
    return text.replace(/\s/g, '');
  }

  function scrollToH2Element(text: string, h2Elements: HTMLHeadingElement[]) {
    let targetElement: HTMLHeadingElement | null = null;

    h2Elements.forEach((element) => {
      if (
        cleanupLanguageText(element.innerText.toLowerCase()) ===
        cleanupLanguageText(text)
      ) {
        targetElement = element as HTMLHeadingElement;
      }
    });

    if (targetElement) {
      // we want to scroll to the top of the element (10% from top of the screen)
      const offset =
        (targetElement as HTMLHeadingElement).offsetTop -
        window.innerHeight * 0.1;

      scrollToPosition(0, offset);
    }
  }

  function trasnformTextToUrlHash(text: string) {
    return `#${hashPrefix}-${decodeURIComponent(text)
      .replaceAll(' ', '_')
      .toLowerCase()}`;
  }

  function transformUrlHashToText(dirtyText: string) {
    const preCleanText = dirtyText.slice(hashPrefix.length + 2); // +2 for the dash and the # char
    return decodeURIComponent(preCleanText.replaceAll('_', ' ').toLowerCase());
  }

  function isSelected(text: string) {
    return (
      !isScrolling &&
      !!stateHash &&
      transformUrlHashToText(stateHash) == text.trim().toLowerCase()
    );
  }

  function navigateTo(text: string) {
    getAndSetH2Elements();
    window.history.replaceState(null, '', trasnformTextToUrlHash(text));
    handleHashChange(h2Elements);
  }

  return (
    <div className='overflow-auto' data-tracker-section='side-navigation'>
      <Text
        variant='label-text-l'
        color='neutral-N50'
        classNames={{ wrapper: 'mb-6 uppercase' }}
      >
        {title}
      </Text>

      <div
        className='flex flex-col pb-px'
        data-tracker-component='side-navigation'
      >
        {headings.map((text) => (
          <div
            className='relative flex items-center'
            key={text}
            data-tracker-component='shortcut'
          >
            <span
              className={cn(
                'bg-neutral-N99 absolute -top-[-1px] left-0.5 h-full rounded-[5px]',
                isSelected(text) ? '-ml-0.5 w-[5px]' : 'w-px'
              )}
            />
            <Button
              onClick={() => !isScrolling && navigateTo(text)}
              variant='text'
              textWrap
              classNames={{
                root: cn(
                  'px-6 py-3 h-auto text-left font-sans hover:text-neutral-N99',
                  isSelected(text) ? 'text-neutral-N99' : 'text-neutral-N50'
                ),
              }}
            >
              {text}
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}
